import React, { useMemo } from "react";
import {
  useFieldArray,
  useFormContext,
  Controller,
  useWatch,
} from "react-hook-form";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Selection from "components/Input/Selection/Selection";
import { Button, Stack, Typography } from "@mui/material";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps, rules } from "../../utils";
import AmexCountryOptions from "utils/AmexOptions/AmexCountryOptions";
import AmexRegionOptions from "utils/AmexOptions/AmexRegionOptions";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";

const SignificantOwnersSection = () => {
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "owners",
  });

  const owners = useWatch({ control, name: "owners" });

  const countryOptions = useMemo(() => {
    return AmexCountryOptions("all");
  }, []);
  const regionOptionsMap = useMemo(() => {
    const map = {};
    owners?.forEach((owner, index) => {
      const countryValue = owner?.country;
      const countryName = countryValue?.split(":")?.[1]?.toUpperCase() ?? "";
      console.log("countryCode", countryName);
      map[index] = AmexRegionOptions(countryName);
    });
    return map;
  }, [owners]);

  return (
    <Stack sx={{ padding: "1rem" }}>
      <Typography variant="h3">
        {t(
          `${PAGE_LOCALE_KEY}processorMdr.amexForm.subtitle.significantOwners`,
        )}
      </Typography>

      {fields.map((owner, index) => {
        const regionOptions = regionOptionsMap[index] || [];

        return (
          <Stack
            key={owner.id}
            sx={{
              border: "1px solid rgb(222, 222, 222)",
              borderRadius: "8px",
              margin: "1rem 0",
              padding: "1rem",
            }}
            gap={2}
          >
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <span>Owner {index + 1}</span>
              <Button
                onClick={() => remove(index)}
                sx={{ display: fields?.length === 1 ? "none" : "flex" }}
              >
                <DeleteOutlinedIcon sx={{ fontSize: 20, color: "red" }} />
              </Button>
            </Stack>

            <Stack gap={2}>
              <Stack direction="row" gap={2} flexWrap={"wrap"}>
                <Controller
                  name={`owners.${index}.lastName`}
                  control={control}
                  rules={rules({
                    maxLength: 20,
                    required: true,
                    validationTags: [
                      "noLeadingWhitespace",
                      "noFullwidthCharacters",
                      "noSpecialSpaces",
                    ],
                  })}
                  defaultValue=""
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <OutlinedText
                      {...CustomComponentCommonProps(
                        t(
                          `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.lastName`,
                        ),
                        value,
                        "OutlinedText",
                        true,
                        true,
                      )}
                      onchangeFn={onChange}
                      divStyle={{ flexGrow: 1 }}
                      sxParams={{
                        textError: error,
                      }}
                      errorMessage={error?.message || ""}
                    />
                  )}
                />
                <Controller
                  name={`owners.${index}.firstName`}
                  control={control}
                  defaultValue=""
                  rules={rules({
                    maxLength: 20,
                    required: true,
                    validationTags: [
                      "noLeadingWhitespace",
                      "noFullwidthCharacters",
                      "noSpecialSpaces",
                    ],
                  })}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <OutlinedText
                      {...CustomComponentCommonProps(
                        t(
                          `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.firstName`,
                        ),
                        value,
                        "OutlinedText",
                        true,
                        true,
                      )}
                      onchangeFn={onChange}
                      divStyle={{ flexGrow: 1 }}
                      sxParams={{
                        textError: error,
                      }}
                      errorMessage={error?.message || ""}
                    />
                  )}
                />
              </Stack>

              <Controller
                name={`owners.${index}.id_no`}
                control={control}
                defaultValue=""
                render={({ field: { value, onChange } }) => (
                  <OutlinedText
                    {...CustomComponentCommonProps(
                      t(
                        `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.idNo`,
                      ),
                      value,
                      "OutlinedText",
                      false,
                      true,
                    )}
                    onchangeFn={onChange}
                    divStyle={{ flexGrow: 1 }}
                  />
                )}
              />
              <Controller
                name={`owners.${index}.dateOfBirth`}
                control={control}
                rules={rules({
                  required: true,
                })}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Datepicker
                    mandatory
                    divStyle={{ flexGrow: 1 }}
                    onchangeFn={onChange}
                    value={value}
                    label={t(
                      `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.dateOfBirth`,
                    )}
                    sxParams={{
                      datepickerError: error,
                    }}
                    errorMessage={error?.message}
                  />
                )}
              />
              <Controller
                name={`owners.${index}.address`}
                control={control}
                defaultValue=""
                rules={rules({
                  maxLength: 200,
                  required: true,
                  validationTags: [
                    "noLeadingWhitespace",
                    "noFullwidthCharacters",
                    "noSpecialSpaces",
                  ],
                })}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <OutlinedText
                    {...CustomComponentCommonProps(
                      t(
                        `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.address`,
                      ),
                      value,
                      "OutlinedText",
                      true,
                      true,
                    )}
                    onchangeFn={onChange}
                    divStyle={{ flexGrow: 1 }}
                    sxParams={{
                      textError: error,
                    }}
                    errorMessage={error?.message}
                  />
                )}
              />

              <Controller
                name={`owners.${index}.postal`}
                control={control}
                defaultValue=""
                render={({ field: { value, onChange } }) => (
                  <OutlinedText
                    {...CustomComponentCommonProps(
                      t(
                        `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.postal`,
                      ),
                      value,
                      "OutlinedText",
                      false,
                      true,
                    )}
                    onchangeFn={onChange}
                    divStyle={{ flexGrow: 1 }}
                  />
                )}
              />

              {/* Country */}
              <Controller
                name={`owners.${index}.country`}
                control={control}
                defaultValue=""
                rules={rules({ required: true })}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Selection
                    {...CustomComponentCommonProps(
                      t(
                        `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.country`,
                      ),
                      value || "",
                      "Selection",
                      true,
                      true,
                    )}
                    options={countryOptions}
                    onchangeFn={onChange}
                    errorMessage={error?.message || ""}
                    sxParams={{
                      selectionError: error,
                    }}
                  />
                )}
              />

              {/* Region */}
              <Controller
                name={`owners.${index}.region`}
                control={control}
                defaultValue=""
                rules={rules({ required: true })}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Selection
                    {...CustomComponentCommonProps(
                      t(
                        `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.owners.region`,
                      ),
                      value || "",
                      "Selection",
                      true,
                      true,
                    )}
                    options={regionOptions}
                    onchangeFn={onChange}
                    errorMessage={error?.message || ""}
                    sxParams={{
                      selectionError: error,
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        );
      })}

      <button
        type="button"
        onClick={() => append({})}
        style={{
          border: "1px dashed black",
          borderRadius: "4px",
          padding: "8px 16px",
          cursor: "pointer",
        }}
      >
        {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.button.addOtherOwner`)}
      </button>
    </Stack>
  );
};

export default React.memo(SignificantOwnersSection);
